import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { X, Heart } from "@phosphor-icons/react";
import { api, avatarUrl } from "@/lib/api";
import Emoji from "@/components/Emoji";
import VerifiedBadge from "@/components/VerifiedBadge";
import { toast } from "sonner";

/**
 * Voxly — LikesSheet.
 *
 * Bottom sheet that lists every user who liked or heart-reacted to a post,
 * with their avatar + name + the emoji they used. Inline Follow / Following
 * button mirrors Instagram's "Likes" sheet so users can quickly start
 * following someone who engaged with their content.
 */
export default function LikesSheet({ postId, open, onClose }) {
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open || !postId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/posts/${postId}/likes`);
        if (alive) setUsers(data?.users || []);
      } catch {
        if (alive) setUsers([]);
      }
    })();
    return () => { alive = false; };
  }, [open, postId]);

  const toggleFollow = async (u) => {
    setBusy(u.user_id);
    try {
      await api.post(`/users/${u.user_id}/follow`);
      setUsers((arr) => arr.map((x) => (x.user_id === u.user_id ? { ...x, following: !x.following } : x)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update follow");
    } finally {
      setBusy(null);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="likes-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-3xl sm:rounded-3xl bg-card border border-border overflow-hidden flex flex-col max-h-[80vh]"
      >
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart size={18} weight="fill" className="text-red-500" />
            <h3 className="font-heading text-lg font-semibold">Liked by</h3>
            {users && <span className="text-xs font-mono text-muted-foreground tabular-nums">{users.length}</span>}
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="likes-sheet-close" className="h-9 w-9 rounded-full bg-secondary/60 flex items-center justify-center">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {users === null && (
            <div className="px-4 py-10 flex justify-center">
              <div className="h-7 w-7 rounded-full border-2 border-primary/60 border-t-transparent animate-spin" />
            </div>
          )}
          {users && users.length === 0 && (
            <div className="px-6 py-12 text-center text-sm text-muted-foreground" data-testid="likes-empty">
              No reactions yet — be the first.
            </div>
          )}
          {users && users.map((u) => (
            <div key={u.user_id} className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/30 transition" data-testid={`likes-row-${u.user_id}`}>
              <Link to={`/u/${u.user_id}`} onClick={onClose} className="shrink-0 relative">
                <img src={avatarUrl(u.picture)} alt="" className="h-11 w-11 rounded-full object-cover" />
                {/* Tiny reaction badge in the corner */}
                <span className="absolute -bottom-0.5 -right-0.5 h-5 w-5 rounded-full bg-card border border-border flex items-center justify-center text-xs">
                  <Emoji char={u.emoji || "❤️"} size={11} />
                </span>
              </Link>
              <Link to={`/u/${u.user_id}`} onClick={onClose} className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate flex items-center gap-1">
                  {u.name}
                  {u.premium && <VerifiedBadge size={12} />}
                </p>
                {u.username && <p className="text-[11px] text-muted-foreground truncate">@{u.username}</p>}
              </Link>
              {!u.is_me && (
                <button
                  onClick={() => toggleFollow(u)}
                  disabled={busy === u.user_id}
                  data-testid={`likes-follow-${u.user_id}`}
                  className={`h-9 px-3 rounded-full text-xs font-bold active:scale-95 transition disabled:opacity-60 ${
                    u.following
                      ? "bg-secondary border border-border text-foreground"
                      : "bg-primary text-primary-foreground"
                  }`}
                >
                  {busy === u.user_id ? "…" : u.following ? "Following" : "Follow"}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
